import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../lib/supabase';
import { Spinner } from './Spinner';

export type ReAuthModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  fieldName: string;
  userEmail: string;
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 10000,
  background: 'var(--modal-overlay-bg)',
  backdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px',
};

const panelStyle: React.CSSProperties = {
  backgroundColor: 'var(--bg-card)',
  border: '1px solid var(--border-color)',
  borderRadius: '16px',
  width: '100%',
  maxWidth: '420px',
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: 'var(--modal-panel-shadow)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  backgroundColor: 'var(--bg-secondary)',
  border: '1px solid var(--border-color)',
  borderRadius: '8px',
  padding: '10px 12px',
  fontSize: '14px',
  color: 'var(--text-primary)',
  outline: 'none',
};

export function ReAuthModal({ open, onClose, onSuccess, fieldName, userEmail }: ReAuthModalProps) {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const passwordId = useId();

  useEffect(() => {
    if (!open) {
      setPassword('');
      setShowPassword(false);
      setError(null);
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const selector =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function getFocusables(): HTMLElement[] {
      const el = modalRef.current;
      if (!el) return [];
      return Array.from(el.querySelectorAll<HTMLElement>(selector)).filter(
        (node) => !node.hasAttribute('disabled')
      );
    }

    const t = window.setTimeout(() => {
      const list = getFocusables();
      list[0]?.focus();
    }, 0);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      const focusables = getFocusables();
      if (e.key !== 'Tab' || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  async function handleConfirm() {
    setError(null);
    if (!password.trim()) {
      setError('Enter your password.');
      return;
    }
    setLoading(true);
    const { error: signErr } = await supabase.auth.signInWithPassword({
      email: userEmail,
      password,
    });
    setLoading(false);
    if (signErr) {
      setError('Incorrect password. Please try again.');
      return;
    }
    setPassword('');
    onSuccess();
    onClose();
  }

  if (!open || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      style={overlayStyle}
      role="presentation"
      onClick={() => {
        if (!loading) onClose();
      }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={panelStyle}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: '20px 24px 16px',
            borderBottom: '1px solid var(--border-color)',
          }}
        >
          <h2
            id={titleId}
            style={{
              fontSize: '18px',
              fontWeight: 700,
              color: 'var(--text-primary)',
              margin: 0,
            }}
          >
            Confirm Your Identity
          </h2>
          <p
            style={{
              margin: '12px 0 0',
              fontSize: '14px',
              lineHeight: 1.5,
              color: 'var(--text-secondary)',
            }}
          >
            To edit your {fieldName}, please confirm your password.
          </p>
        </div>

        <div style={{ padding: '20px 24px' }}>
          <label
            htmlFor={passwordId}
            style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: 500,
              color: 'var(--text-secondary)',
              marginBottom: '8px',
            }}
          >
            Password
          </label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
            <input
              id={passwordId}
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(null);
              }}
              style={{ ...inputStyle, flex: 1 }}
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              disabled={loading}
              style={{
                flexShrink: 0,
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                fontSize: '13px',
                fontWeight: 500,
                padding: '0 12px',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          {error ? (
            <p
              role="alert"
              style={{
                marginTop: '12px',
                fontSize: '13px',
                color: 'var(--accent-danger)',
              }}
            >
              {error}
            </p>
          ) : null}
        </div>

        <div
          style={{
            padding: '16px 24px 20px',
            borderTop: '1px solid var(--border-color)',
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            style={{
              background: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={() => void handleConfirm()}
            style={{
              background: 'var(--accent-green)',
              color: 'var(--text-on-accent)',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              opacity: loading ? 0.85 : 1,
            }}
          >
            {loading ? (
              <>
                <Spinner className="h-4 w-4 text-cta-text" />
                Verifying…
              </>
            ) : (
              'Confirm Identity'
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
